// Dropping a file onto a page that does not handle the drop makes the browser
// navigate to it: the CV upload box looked like it "opened a download dialog"
// instead of uploading (issue #254). Handling `dragover` is what makes a drop
// zone a drop zone - without preventDefault there, the drop never fires.
//
// Pure helpers so the accept/extension logic is testable without a DOM.

export type DropFileFilter = {
  /** Same syntax as an <input accept> attribute, e.g. ".pdf,.docx,image/*". */
  accept?: string;
};

function parseAcceptList(accept?: string) {
  return (accept ?? '')
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean);
}

/** Mirrors how a browser matches <input accept> against a picked file. */
export function isFileAccepted(
  file: { name: string; type?: string },
  accept?: string,
) {
  const patterns = parseAcceptList(accept);
  if (patterns.length === 0) return true;

  const name = (file.name || '').toLowerCase();
  const mime = (file.type || '').toLowerCase();

  return patterns.some((pattern) => {
    if (pattern.startsWith('.')) return name.endsWith(pattern);
    if (pattern.endsWith('/*')) {
      return mime.startsWith(`${pattern.slice(0, -1)}`);
    }
    return mime === pattern;
  });
}

type DropCandidate = { name: string; type?: string };

/**
 * Picks the first acceptable file from a drop. Returns null when the drop
 * carried nothing usable, so callers can show a message instead of silently
 * doing nothing.
 */
export function pickDroppedFile<T extends DropCandidate>(
  files: readonly T[] | null | undefined,
  accept?: string,
): T | null {
  if (!files || files.length === 0) return null;
  return files.find((file) => isFileAccepted(file, accept)) ?? null;
}

export function hasDraggedFiles(types: readonly string[] | undefined | null) {
  return Boolean(types?.includes('Files'));
}

/** Accepted resume uploads, shared by the file picker and the drop zone. */
export const RESUME_FILE_ACCEPT = '.pdf,.docx,.jpg,.jpeg,.png,.txt';
